import { CalendarDate, type DateValue, endOfMonth, toCalendarDate } from "@internationalized/date";
import type { WhenDatePart, WhenDurationPreset, WhenSelection } from "@ui/components/SearchBar";

/** A resolved date window for the programs "When" facet. ISO `yyyy-MM-dd` bounds,
 *  either of which may be absent (open-ended). */
export interface SearchWindow {
  gte?: string;
  lte?: string;
}

// Duration presets the When popover offers for the "end" leg, expressed as an
// offset from the start anchor.
const DURATION_DAYS: Partial<Record<WhenDurationPreset, number>> = {
  overnight: 1,
  "3-days": 3,
  "1-week": 7,
  "2-weeks": 14,
};
const DURATION_MONTHS: Partial<Record<WhenDurationPreset, number>> = {
  "1-month": 1,
  "3-months": 3,
};

function toCalendar(value: DateValue): CalendarDate {
  return toCalendarDate(value);
}

/** The lower bound (start anchor) of the selection, if any. */
function startAnchor(start: WhenDatePart | null): CalendarDate | null {
  if (!start) return null;
  if (start.kind === "month") return new CalendarDate(start.year, start.month, 1);
  if (start.kind === "date") return toCalendar(start.value);
  return null;
}

/** The upper bound of the selection. A duration end is measured from the start
 *  anchor; a month-only start with no end spans the whole month. */
function endBound(value: WhenSelection, anchor: CalendarDate | null): CalendarDate | null {
  const { start, end } = value;
  if (end?.kind === "date") return toCalendar(end.value);
  if (end?.kind === "month") return endOfMonth(new CalendarDate(end.year, end.month, 1));
  if (end?.kind === "duration" && anchor) {
    const days = DURATION_DAYS[end.preset];
    if (days !== undefined) return anchor.add({ days });
    const months = DURATION_MONTHS[end.preset];
    if (months !== undefined) return anchor.add({ months });
  }
  if (!end && start?.kind === "month" && anchor) return endOfMonth(anchor);
  return null;
}

/**
 * Maps a SearchBar `WhenSelection` to a concrete `[gte, lte]` date window for the
 * programs search filter. Returns `null` when nothing usable is selected (no
 * filter). Either bound may be omitted for an open-ended window.
 */
export function whenSelectionToWindow(value: WhenSelection): SearchWindow | null {
  const anchor = startAnchor(value.start);
  const end = endBound(value, anchor);
  // Normalise an inverted selection (end before start) so the range filter never
  // emits gte > lte, which matches nothing in OpenSearch and shows an unexplained
  // empty grid.
  const [lower, upper] = anchor && end && anchor.compare(end) > 0 ? [end, anchor] : [anchor, end];
  const window: SearchWindow = {};
  if (lower) window.gte = lower.toString();
  if (upper) window.lte = upper.toString();
  return window.gte || window.lte ? window : null;
}
